import React, { useEffect, useMemo, useCallback, useState } from 'react';
import styles from './SuperUnitEdit.module.scss';
import Header from '../../../../components/header/Header';
import { useParams } from 'react-router-dom';
import { Lesson, Content } from '@types';
import Spinner from '../../../../components/spinner/Spinner';
import { EditUnitProvider, useEditUnit } from './useEditUnit';
import Preparations from '../preparations/Preparations';
import Presentation from '../presentations/Presentations';
import EditUnitContent from './edit-unit/EditUnitContent';
import EditLessonContent from './edit-lesson/EditLessonContent';
import EditContent from './edit-content/EditContent';
import { useGetUnitInfoForSuperAdminLazyQuery, useHeaGetUnitInfoForSuperAdminLazyQuery, LessonType as TeacherLessonType } from '@api/graphql-codegen';
import ArrowTop from 'src/assets/icons/chewron_top.svg?react';
import IconPencil from 'src/assets/icons/pencil.svg?react';
import { notEmpty } from '@utils/helpers';
import FooterPage from '@src/components/footer-page/FooterPage';

type Props = {};
type Params = {
  id: string;
}

function sortByNumber(a: { number?: number | null } | null, b: { number?: number | null } | null) {
  return (a?.number ?? 10000) - (b?.number ?? 0);
}

function SuperUnitEdit(props: Props) {
  const { id } = useParams<Params>();
  const [opened, setOpened] = useState(true);

  const { unit, setUnit, heaUnit, setHeaUnit, setSelectionLevel, selectionLevel, setContent, setLesson} = useEditUnit();

  const [getUnitInfo, { loading, data: unitData, refetch }] = useGetUnitInfoForSuperAdminLazyQuery({ fetchPolicy: 'network-only' });
  const [getHeaUnitInfo, { loading: heaLoading, data: heaUnitData, refetch: refetchHea }] = useHeaGetUnitInfoForSuperAdminLazyQuery({ fetchPolicy: 'network-only' });

  useEffect(() => {
    if (id) {
      getUnitInfo({
        variables: {
          input: { id }
        }
      });
      getHeaUnitInfo({
        variables: {
          input: { id }
        }
      });
    }
  }, [getUnitInfo, getHeaUnitInfo, id]);

  useEffect(() => {
    if (unitData?.getUnitInfoForSuperAdmin?.id) {
      setUnit(unitData.getUnitInfoForSuperAdmin);
    }
  }, [setUnit, unitData?.getUnitInfoForSuperAdmin]);

  useEffect(() => {
    if (heaUnitData?.heaGetUnitInfoForSuperAdmin?.id) {
      setHeaUnit(heaUnitData?.heaGetUnitInfoForSuperAdmin);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heaUnitData?.heaGetUnitInfoForSuperAdmin?.id, setHeaUnit]);

  useEffect(() => {
    if (unitData?.getUnitInfoForSuperAdmin?.id) {
      handleSelectUnit();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitData?.getUnitInfoForSuperAdmin?.id]);

  const handleSelectUnit = useCallback(() => {
    if (unitData?.getUnitInfoForSuperAdmin?.id) {
      setUnit(unitData?.getUnitInfoForSuperAdmin);
      setSelectionLevel('Unit');
      setContent(undefined);
      setLesson(undefined);
    }
    if (heaUnitData?.heaGetUnitInfoForSuperAdmin?.id) {
      setHeaUnit(heaUnitData?.heaGetUnitInfoForSuperAdmin);
    }
  }, [unitData?.getUnitInfoForSuperAdmin, heaUnitData?.heaGetUnitInfoForSuperAdmin, setUnit, setSelectionLevel, setContent, setLesson, setHeaUnit]);

  const handleClose = useCallback((e : React.SyntheticEvent) => {
    e.stopPropagation();
    setOpened(state => !state);
  }, []);

  const handleRefetchUnit = useCallback(async () => {
    await Promise.all([refetch(), heaUnit ? refetchHea() : null]);
  },[heaUnit, refetch, refetchHea]);

  const listwrapper = useMemo(() => {
    const classes = [styles.wrapper];

    if (opened) {
      classes.push(styles.opened);
    }

    return classes.join(' ');
  }, [opened]);

  const contentClasses = useMemo(() => {
    const classes = [styles.list];

    return classes.join(' ');
  }, []);

  const content = useMemo(() => {
    switch (selectionLevel) {
      case 'Unit': {
        return <EditUnitContent refetchUnit={handleRefetchUnit}/>;
      }
      case 'Lesson': {
        return <EditLessonContent refetchUnit={handleRefetchUnit}/>;
      }
      case 'Content':
      case 'SectionContent': {
        return <EditContent />;
      }
      default: {
        return null;
      }
    }
  }, [selectionLevel, handleRefetchUnit]);

  const tPreps: Content[] = useMemo(() => [...unit?.teacherPreparation || []].filter(notEmpty), [unit?.teacherPreparation]);
  const alternateMaterials: Content[] = useMemo(() => [...unit?.alternateMaterials || []].filter(notEmpty), [unit?.alternateMaterials]);
  const pPreps = useMemo(() => [...heaUnit?.parentPreparation || []].filter(notEmpty), [heaUnit?.parentPreparation]);
  const cPres: Lesson[] = useMemo(() => {
    const newCPres = [...unit?.classPresentation || []];
    newCPres.sort(sortByNumber);
    const updatedCPres: Lesson[] = newCPres.filter(notEmpty);
    return updatedCPres;
  }, [unit?.classPresentation]);
  const studyGuides: Content[] = useMemo(() => [...unit?.studyGuides || []].filter(notEmpty), [unit?.studyGuides]);
  const studentQuizzes: Content[] = useMemo(() => [...unit?.studentQuizzes || []].filter(notEmpty), [unit?.studentQuizzes]);
  const studyGuideMaterials: Content[] = useMemo(() => [...unit?.studyGuideMaterials || []].filter(notEmpty), [unit?.studyGuideMaterials]);

  return (
    <>
      <Header />
      {(loading || heaLoading) ? <div className={styles.loading}><Spinner/></div> :
        <div className={styles.page}>
          <div className={styles.mainWrapper}>
            <div className={listwrapper}>
              <div onClick={handleSelectUnit} className={styles.programName}>
                <div className={styles.bolderPN}>
                  Edit Unit<br/><span className={styles.ligtherPN}>{unit?.name || ''}</span>
                </div>
              </div>
              <section onClick={handleClose} className={styles.header}>
                <div className={styles.headerContent}>
                  <div className={styles.title}>{unit?.name || ''}</div>
                </div>
                <div className={styles.iconWrapper} onClick={handleClose}>
                  <ArrowTop className={styles.icon}/>
                </div>
                <div className={styles.editIconWrapper} onClick={handleSelectUnit}>
                  <IconPencil/>
                </div>
              </section>
              <section className={contentClasses}>
                <Preparations
                  lessonOpened={false}
                  lessonType={TeacherLessonType.PREPARATION}
                  contents={tPreps}
                  title="Course Materials"
                />
                <Preparations
                  lessonOpened={false}
                  lessonType={TeacherLessonType.ALTERNATE}
                  contents={alternateMaterials}
                  title="Alternative Course Materials"
                />
                <Preparations
                  lessonOpened={false}
                  lessonType={TeacherLessonType.STUDYGUIDE}
                  contents={studyGuides}
                  materialContents={studyGuideMaterials}
                  title="Unit Study Review"
                />
                <Preparations
                  lessonOpened={false}
                  lessonType={TeacherLessonType.STUDENTQUIZ}
                  contents={studentQuizzes}
                  title="Student Quiz Questions"
                />
                {heaUnit ? (
                  <Preparations
                    lessonOpened={false}
                    lessonType={TeacherLessonType.PREPARATION}
                    heaModules={pPreps}
                    title="Parent Preparation"
                    isHEPreparation
                  />
                ) : null}
                <Presentation
                  lessons={cPres}
                  unitId={unit?.id!}
                  title={'Class Presentation'}
                  refetchUnit={handleRefetchUnit}
                  refetchSchoolUnit={refetch}
                />
              </section>
            </div>
            <div className={styles.content}>
              {content}
            </div>
          </div>
          <FooterPage />
        </div>
      }
    </>
  );
}

const EditUnit = (props: Props) => {
  return (
    <EditUnitProvider>
      <SuperUnitEdit {...props}/>
    </EditUnitProvider>
  )
};

export default EditUnit;
